import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Switch,
  Table,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import {
  FaBars,
  FaBullhorn,
  FaChartPie,
  FaUsers,
  FaSitemap,
  FaTags,
  FaShareAlt,
  FaMapMarkedAlt,
  FaCoins,
  FaSignOutAlt,
  FaCog,
  FaMoneyBillWave
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../../api";
import MapaCameras from "../../components/MapaCameras";
import EmpresaBrandingConfig from "./EmpresaBrandingConfig";
import LandingAdminPage from "./LandingAdminPage";

const MotionBox = motion.create(Box);

const menuItems = [
  { id: "resumo", label: "Visão geral", icon: FaChartPie },
  { id: "clientes", label: "Clientes", icon: FaUsers },
  { id: "afiliadas", label: "Afiliadas", icon: FaSitemap },
  { id: "planos", label: "Planos", icon: FaTags },
  { id: "compartilhamentos", label: "Compartilhamentos", icon: FaShareAlt },
  { id: "mapa", label: "Mapa global", icon: FaMapMarkedAlt },
  { id: "financeiro", label: "Financeiro", icon: FaMoneyBillWave },
  { id: "comissoes", label: "Comissões", icon: FaCoins },
  { id: "landing", label: "Landing", icon: FaBullhorn },
  { id: "config", label: "Configurações", icon: FaCog }
];

const Pagination = ({ page, pageSize, total, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <HStack justify="space-between" py={3}>
      <Text fontSize="sm" color="gray.500">
        Página {page} de {totalPages} - {total} registros
      </Text>
      <HStack>
        <Button size="sm" onClick={() => onPageChange(Math.max(1, page - 1))} isDisabled={page <= 1}>
          Anterior
        </Button>
        <Button
          size="sm"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          isDisabled={page >= totalPages}
        >
          Próxima
        </Button>
      </HStack>
    </HStack>
  );
};

const Card = ({ title, value, help, colorScheme = "green" }) => (
  <Stat className="empresa-kpi-card" p={4}>
    <StatLabel className="empresa-kpi-label">{title}</StatLabel>
    <StatNumber color={`${colorScheme}.200`}>{value}</StatNumber>
    {help && <StatHelpText>{help}</StatHelpText>}
  </Stat>
);

const EmpresaResumo = () => {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({
    clientes: 0,
    camerasAtivas: 0,
    ocorrencias: 0,
    alertas: 0,
    receita: 0
  });

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get("/v2/mapa");
        const usuarios = response.data.usuarios || [];
        const ocorrencias = response.data.ocorrencias || [];
        let camerasAtivas = 0;
        usuarios.forEach((user) => {
          (user.cameras || []).forEach((cam) => {
            if (Number(cam.ativa) === 1 || cam.ativa === true) {
              camerasAtivas += 1;
            }
          });
        });
        const alertas = ocorrencias.filter((item) => item.tipo === "Panico").length;
        setKpis({
          clientes: usuarios.length,
          camerasAtivas,
          ocorrencias: ocorrencias.length,
          alertas,
          receita: usuarios.length * 99
        });
      } catch (err) {
        console.error("Erro ao carregar KPIs:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  return (
    <Stack spacing={6}>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
        {loading ? (
          Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton key={idx} height="110px" borderRadius="xl" />
          ))
        ) : (
          <>
            <Card title="Clientes" value={kpis.clientes} help="Ativos no tenant" />
            <Card title="Câmeras ativas" value={kpis.camerasAtivas} help="Streaming OK" colorScheme="blue" />
            <Card title="Ocorrências" value={kpis.ocorrencias} help="Últimos registros" colorScheme="orange" />
            <Card title="Alertas" value={kpis.alertas} help="Pânico ativo" colorScheme="red" />
            <Card title="Receita estimada" value={`R$ ${kpis.receita.toFixed(2)}`} help="Base atual" />
          </>
        )}
      </SimpleGrid>

      <Box className="empresa-panel-card" p={5}>
        <Text fontWeight="bold" mb={2}>
          Resumo operacional
        </Text>
        <Text color="rgba(221, 247, 239, 0.9)" fontSize="sm">
          Use este painel para acompanhar limites de planos, crescimento de afiliadas e desempenho de
          monitoramento. Os dados são atualizados via tenant e ACL.
        </Text>
      </Box>
    </Stack>
  );
};
const EmpresaConfig = ({ empresaId }) => <EmpresaBrandingConfig empresaId={empresaId} />;

const EmpresaClientes = () => {
  const navigate = useNavigate();
  const localUser = useMemo(() => JSON.parse(localStorage.getItem("user") || "{}"), []);
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0 });
  const [filters, setFilters] = useState({ q: "", status: "" });
  const [editing, setEditing] = useState(null);
  const [modalTabIndex, setModalTabIndex] = useState(0);
  const [camerasCliente, setCamerasCliente] = useState([]);
  const [camerasLoading, setCamerasLoading] = useState(false);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [fotoCliente, setFotoCliente] = useState(null);
  const [form, setForm] = useState({
    nome: "",
    email: "",
    senha: "",
    endereco: "",
    cidade: "",
    cidades_permitidas: [],
    latitude: "",
    longitude: "",
    plano_id: "",
    periodo: "mensal",
    data_inicio: "",
    data_fim: "",
    valor: ""
  });
  const [geocodeLoading, setGeocodeLoading] = useState(false);
  const lastGeocodeRef = useRef("");
  const modal = useDisclosure();
  const toast = useToast();
  const planoMap = useMemo(() => {
    const map = new Map();
    planos.forEach((plano) => {
      map.set(String(plano.id), plano.nome);
    });
    return map;
  }, [planos]);

  const uniqueCityOptions = useMemo(() => {
    const seen = new Set();
    return cityOptions.filter((city) => {
      const normalized = String(city?.cidade || "").trim().toLowerCase();
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
  }, [cityOptions]);

  const resolvePlanoPreco = (plano, periodo) => {
    if (!plano) return null;
    if (periodo === "trimestral") return plano.preco_trimestral ?? plano.preco_mensal ?? plano.preco;
    if (periodo === "anual") return plano.preco_anual ?? plano.preco_mensal ?? plano.preco;
    return plano.preco_mensal ?? plano.preco;
  };

  const resolveDataFim = (inicio, periodo) => {
    if (!inicio) return "";
    const date = new Date(`${inicio}T00:00:00`);
    if (Number.isNaN(date.getTime())) return "";
    const months = periodo === "trimestral" ? 3 : periodo === "anual" ? 12 : 1;
    const next = new Date(date);
    const day = next.getDate();
    next.setMonth(next.getMonth() + months);
    if (next.getDate() < day) {
      next.setDate(0);
    }
    return next.toISOString().slice(0, 10);
  };

  const geocodeEndereço = async (endereco) => {
    const enderecoLimpo = String(endereco || "").trim();
    if (enderecoLimpo.length < 6) return;
    if (lastGeocodeRef.current === enderecoLimpo) return;
    lastGeocodeRef.current = enderecoLimpo;
    setGeocodeLoading(true);

    const key =
      process.env.REACT_APP_GOOGLE_MAPS_KEY || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

    try {
      if (key) {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            enderecoLimpo
          )}&key=${key}`
        );
        const data = await response.json();
        if (data.status === "OK" && data.results?.length) {
          const { lat, lng } = data.results[0].geometry.location;
          setForm((prev) => ({
            ...prev,
            latitude: Number(lat).toFixed(6),
            longitude: Number(lng).toFixed(6)
          }));
          return;
        }
      }

      const fallback = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(enderecoLimpo)}`
      );
      const fallbackData = await fallback.json();
      if (fallbackData?.length) {
        setForm((prev) => ({
          ...prev,
          latitude: Number(fallbackData[0].lat).toFixed(6),
          longitude: Number(fallbackData[0].lon).toFixed(6)
        }));
        return;
      }

      toast({ title: "Endereço não encontrado", status: "warning", duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: "Erro ao buscar coordenadas", status: "error", duration: 3000, isClosable: true });
    } finally {
      setGeocodeLoading(false);
    }
  };

  const load = async (page = pagination.page) => {
    setLoading(true);
    try {
      const [clientesRes, planosRes, cidadesRes] = await Promise.all([
        api.get("/v2/usuarios", {
          params: {
            role: "cliente",
            page,
            page_size: pagination.page_size,
            q: filters.q || undefined,
            status: filters.status || undefined
          }
        }),
        api.get("/v2/planos", { params: { page: 1, page_size: 100 } }),
        api.get("/v2/branding/cidades")
      ]);
      setClientes(clientesRes.data.items || []);
      setPagination({
        page: clientesRes.data.page,
        page_size: clientesRes.data.page_size,
        total: clientesRes.data.total
      });
      setPlanos(planosRes.data.items || []);
      setCityOptions(Array.isArray(cidadesRes.data) ? cidadesRes.data : []);
    } catch (err) {
      console.error("Erro ao carregar clientes:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, []);

  const loadCameras = async (usuarioId) => {
    if (!usuarioId) {
      setCamerasCliente([]);
      return;
    }
    setCamerasLoading(true);
    try {
      const response = await api.get(`/cameras/usuario/${usuarioId}`);
      setCamerasCliente(response.data || []);
    } catch (err) {
      toast({ title: "Erro ao carregar câmeras", status: "error", duration: 3000, isClosable: true });
    } finally {
      setCamerasLoading(false);
    }
  };

  const handleCameraChange = (id, field, value) => {
    setCamerasCliente((prev) =>
      prev.map((cam) => (cam.id === id ? { ...cam, [field]: value } : cam))
    );
  };

  const formatHora = (value) => {
    if (!value) return "";
    return String(value).slice(0, 5);
  };

  const handleCameraSave = async (camera) => {
    try {
      await api.put(`/cameras/editar/${camera.id}`, {
        nome: camera.nome,
        ativa: camera.ativa ? 1 : 0,
        flag_publico: camera.flag_publico,
        ia_ativo: camera.ia_ativo ? 1 : 0,
        ia_hora_inicio: camera.ia_hora_inicio || null,
        ia_hora_fim: camera.ia_hora_fim || null
      });
      toast({ title: "Câmera atualizada", status: "success", duration: 3000, isClosable: true });
      await loadCameras(editing?.id || null);
    } catch (err) {
      toast({ title: "Erro ao atualizar camera", status: "error", duration: 3000, isClosable: true });
    }
  };

  const handleFotoClienteChange = (event) => {
    setFotoCliente(event.target.files?.[0] || null);
  };

  useEffect(() => {
    if (!form.plano_id) return;
    const plano = planos.find((item) => String(item.id) === String(form.plano_id));
    if (!plano) return;
    if (!form.valor) {
      const preco = resolvePlanoPreco(plano, form.periodo);
      if (preco != null) {
        setForm((prev) => ({ ...prev, valor: String(preco) }));
      }
    }
  }, [form.plano_id, form.periodo, planos]);

  useEffect(() => {
    if (!form.data_inicio || form.data_fim) return;
    const dataFim = resolveDataFim(form.data_inicio, form.periodo);
    if (dataFim) {
      setForm((prev) => ({ ...prev, data_fim: dataFim }));
    }
  }, [form.data_inicio, form.periodo, form.data_fim]);

  const openNew = () => {
    setEditing(null);
    setModalTabIndex(0);
    setCamerasCliente([]);
    setFotoCliente(null);
    setUserDetailLoading(false);
    setForm({
      nome: "",
      email: "",
      senha: "",
      endereco: "",
      cidade: "",
      cidades_permitidas: [],
      latitude: "",
      longitude: "",
      plano_id: "",
      periodo: "mensal",
      data_inicio: "",
      data_fim: "",
      valor: ""
    });
    modal.onOpen();
  };

  const toggleCidadePermitida = (cidade) => {
    const normalized = String(cidade || "").trim();
    if (!normalized) return;
    setForm((prev) => {
      const exists = prev.cidades_permitidas.includes(normalized);
      return {
        ...prev,
        cidades_permitidas: exists
          ? prev.cidades_permitidas.filter((item) => item !== normalized)
          : [...prev.cidades_permitidas, normalized]
      };
    });
  };

  const openEdit = async (cliente, initialTab = 0) => {
    setEditing(cliente);
    setModalTabIndex(initialTab);
    loadCameras(cliente.id);
    setFotoCliente(null);
    setUserDetailLoading(true);
    setForm({
      nome: cliente.nome || "",
      email: cliente.email || "",
      senha: "",
      endereco: cliente.endereco || "",
      cidade: cliente.cidade || "",
      cidades_permitidas: cliente.cidade ? [cliente.cidade] : [],
      latitude: cliente.latitude || "",
      longitude: cliente.longitude || "",
      plano_id: cliente.assinatura_plano_id || cliente.plano_id || "",
      periodo: cliente.assinatura_periodo || "mensal",
      data_inicio: cliente.data_inicio ? cliente.data_inicio.slice(0, 10) : "",
      data_fim: cliente.data_fim ? cliente.data_fim.slice(0, 10) : "",
      valor: cliente.assinatura_valor ? String(cliente.assinatura_valor) : ""
    });
    modal.onOpen();
    try {
      const detailRes = await api.get(`/v2/usuarios/${cliente.id}`);
      const detail = detailRes.data || {};
      setForm((prev) => ({
        ...prev,
        nome: detail.nome || prev.nome,
        email: detail.email || prev.email,
        endereco: detail.endereco || prev.endereco,
        cidade: detail.cidade || prev.cidade,
        cidades_permitidas: Array.isArray(detail.cidades_permitidas) ? detail.cidades_permitidas : [],
        latitude: detail.latitude || prev.latitude,
        longitude: detail.longitude || prev.longitude,
        plano_id: detail.plano_id || prev.plano_id
      }));
    } catch (err) {
      toast({ title: "Erro ao carregar detalhes do cliente", status: "error", duration: 3000, isClosable: true });
    } finally {
      setUserDetailLoading(false);
    }
  };

  const openClienteArea = (cliente) => {
    const tenantId = Number(cliente?.empresa_id || localUser?.empresa_id);
    if (Number.isInteger(tenantId) && tenantId > 0) {
      localStorage.setItem("active_empresa_id", String(tenantId));
      localStorage.setItem("empresa_scope", "self");
    }
    localStorage.setItem("dashboard_target_tab", "mapa");
    localStorage.setItem("preview_cliente_id", String(cliente.id));
    navigate("/sistema/dashboard");
  };

  const handleSave = async () => {
    try {
      const cidadesPermitidas = Array.from(
        new Set([...(form.cidades_permitidas || []), form.cidade].map((item) => String(item || "").trim()).filter(Boolean))
      );
      const payload = {
        nome: form.nome,
        email: form.email,
        endereco: form.endereco,
        cidade: form.cidade || null,
        cidades_permitidas: cidadesPermitidas,
        latitude: form.latitude || null,
        longitude: form.longitude || null,
        plano_id: form.plano_id || null
      };

      let usuarioId = editing?.id;
      if (!editing) {
        payload.senha = form.senha;
        const response = await api.post("/v2/usuarios", payload);
        usuarioId = response.data?.id;
      } else {
        await api.put(`/v2/usuarios/${editing.id}`, payload);
      }

      if (form.plano_id && usuarioId) {
        const dataFimFinal = form.data_fim || resolveDataFim(form.data_inicio, form.periodo);
        await api.post("/v2/assinaturas", {
          usuario_id: usuarioId,
          plano_id: form.plano_id,
          periodo: form.periodo || "mensal",
          data_inicio: form.data_inicio || null,
          data_fim: dataFimFinal || null,
          valor: form.valor || null
        });
        if (!editing) {
          await api.post(`/v2/usuarios/${usuarioId}/gerar-cameras`);
        }
      }

      if (fotoCliente && usuarioId) {
        const upload = new FormData();
        upload.append("foto", fotoCliente);
        await api.post(`/usuarios/upload-foto/${usuarioId}`, upload, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        setFotoCliente(null);
      }

      toast({ title: "Cliente salvo", status: "success", duration: 3000, isClosable: true });
      modal.onClose();
      load();
    } catch (err) {
      toast({ title: "Erro ao salvar", status: "error", duration: 3000, isClosable: true });
    }
  };

  const handleDelete = async (cliente) => {
    try {
      await api.delete(`/v2/usuarios/${cliente.id}`);
      toast({ title: "Cliente desativado", status: "success", duration: 3000, isClosable: true });
      load();
    } catch (err) {
      toast({ title: "Erro ao desativar", status: "error", duration: 3000, isClosable: true });
    }
  };

  return (
    <Stack spacing={4}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Text fontWeight="bold" fontSize="lg">
          Clientes
        </Text>
        <HStack spacing={2} flexWrap="wrap">
          <Input
            placeholder="Buscar por nome/email"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="empresa-input-control"
          />
          <Select
            placeholder="Status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            maxW="160px"
            className="empresa-input-control"
          >
            <option value="ativo">Ativo</option>
            <option value="suspenso">Suspenso</option>
          </Select>
          <Button size="sm" className="empresa-action-btn" onClick={() => load(1)}>
            Filtrar
          </Button>
          <Button size="sm" className="empresa-primary-btn" onClick={openNew}>
            Novo cliente
          </Button>
        </HStack>
      </Flex>
      <Box className="empresa-panel-card" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Nome</Th>
              <Th>Email</Th>
              <Th>Status</Th>
              <Th>Plano</Th>
              <Th>Periodo</Th>
              <Th>Inicio</Th>
              <Th>Fim</Th>
              <Th>Acoes</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={8}>
                  <Skeleton height="20px" />
                </Td>
              </Tr>
            ) : (
              clientes.map((cliente) => (
                <Tr key={cliente.id}>
                  <Td>{cliente.nome}</Td>
                  <Td>{cliente.email}</Td>
                  <Td>
                    <Badge colorScheme={cliente.status === "ativo" ? "green" : "gray"}>
                      {cliente.status || "ativo"}
                    </Badge>
                  </Td>
                  <Td>
                    {planoMap.get(String(cliente.assinatura_plano_id || cliente.plano_id)) ||
                      cliente.assinatura_plano_id ||
                      cliente.plano_id ||
                      "-"}
                  </Td>
                  <Td>{cliente.assinatura_periodo || "-"}</Td>
                  <Td>{cliente.data_inicio ? cliente.data_inicio.slice(0, 10) : "-"}</Td>
                  <Td>{cliente.data_fim ? cliente.data_fim.slice(0, 10) : "-"}</Td>
                  <Td>
                    <HStack spacing={1.5} flexWrap="wrap">
                      <Button size="xs" className="empresa-action-btn" onClick={() => openEdit(cliente, 0)}>
                        Editar
                      </Button>
                      <Button size="xs" className="empresa-action-btn" onClick={() => openEdit(cliente, 1)}>
                        Câmeras
                      </Button>
                      <Button size="xs" className="empresa-action-btn" onClick={() => openClienteArea(cliente)}>
                        Area cliente
                      </Button>
                      <Button size="xs" colorScheme="red" onClick={() => handleDelete(cliente)}>
                        Excluir
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
        <Pagination
          page={pagination.page}
          pageSize={pagination.page_size}
          total={pagination.total}
          onPageChange={(next) => load(next)}
        />
      </Box>

      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size="4xl">
        <ModalOverlay bg="rgba(1, 10, 18, 0.78)" backdropFilter="blur(3px)" />
        <ModalContent
          className="empresa-modal-content"
          sx={{
            background:
              "radial-gradient(circle at 10% -14%, rgba(113, 247, 238, 0.16), transparent 36%), linear-gradient(166deg, rgba(7, 57, 75, 0.96), rgba(4, 29, 43, 0.99))",
            border: "1px solid rgba(183, 246, 241, 0.32)",
            color: "#effff9"
          }}
        >
          <ModalHeader>{editing ? "Editar cliente" : "Novo cliente"}</ModalHeader>
          <ModalBody>
            <Tabs
              variant="soft-rounded"
              colorScheme="teal"
              index={modalTabIndex}
              onChange={(index) => setModalTabIndex(index)}
            >
              <TabList>
                <Tab>Dados</Tab>
                <Tab isDisabled={!editing}>Câmeras</Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  <Stack spacing={3}>
                    {userDetailLoading ? <Skeleton height="42px" /> : null}
                    <FormControl isRequired>
                      <FormLabel>Nome</FormLabel>
                      <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Email</FormLabel>
                      <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Foto do cliente</FormLabel>
                      <Input type="file" accept="image/*" onChange={handleFotoClienteChange} />
                      {fotoCliente && (
                        <Image
                          src={URL.createObjectURL(fotoCliente)}
                          alt="Preview"
                          boxSize="80px"
                          borderRadius="full"
                          mt={2}
                        />
                      )}
                    </FormControl>
                    {!editing && (
                      <FormControl isRequired>
                        <FormLabel>Senha</FormLabel>
                        <Input
                          type="password"
                          value={form.senha}
                          onChange={(e) => setForm({ ...form, senha: e.target.value })}
                        />
                      </FormControl>
                    )}
                      <FormControl>
                        <FormLabel>Cidade</FormLabel>
                        <Input
                          value={form.cidade}
                          onChange={(e) => setForm({ ...form, cidade: e.target.value })}
                          placeholder="Ex.: Cabo Verde"
                        />
                      </FormControl>
                      <FormControl>
                        <HStack justify="space-between" align="center" mb={2} flexWrap="wrap">
                          <FormLabel mb={0}>Cidades permitidas</FormLabel>
                          <HStack spacing={2}>
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() =>
                                setForm((prev) => ({
                                  ...prev,
                                  cidades_permitidas: uniqueCityOptions.map((item) => item.cidade)
                                }))
                              }
                              isDisabled={!uniqueCityOptions.length}
                            >
                              Selecionar todas
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => setForm((prev) => ({ ...prev, cidades_permitidas: prev.cidade ? [prev.cidade] : [] }))}
                            >
                              Limpar extras
                            </Button>
                          </HStack>
                        </HStack>
                        <Text fontSize="sm" color="whiteAlpha.700" mb={3}>
                          A cidade principal será incluída automaticamente no acesso do usuário.
                        </Text>
                        {uniqueCityOptions.length ? (
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                            {uniqueCityOptions.map((city) => (
                              <Checkbox
                                key={`${city.empresa_id}-${city.id}`}
                                isChecked={form.cidades_permitidas.includes(city.cidade)}
                                onChange={() => toggleCidadePermitida(city.cidade)}
                                colorScheme="teal"
                              >
                                {city.cidade}{city.estado ? ` - ${city.estado}` : ""}
                              </Checkbox>
                            ))}
                          </SimpleGrid>
                        ) : (
                          <Text fontSize="sm" color="whiteAlpha.700">
                            Nenhuma cidade configurada no white-label da empresa.
                          </Text>
                        )}
                      </FormControl>
                      <FormControl>
                        <FormLabel>Endereço</FormLabel>
                      <Input
                        value={form.endereco}
                        onChange={(e) => setForm({ ...form, endereco: e.target.value })}
                        onBlur={() => geocodeEndereço(form.endereco)}
                      />
                      <Button
                        mt={2}
                        size="xs"
                        variant="outline"
                        colorScheme="blue"
                        isLoading={geocodeLoading}
                        onClick={() => geocodeEndereço(form.endereco)}
                      >
                        Buscar coordenadas
                      </Button>
                    </FormControl>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                      <FormControl>
                        <FormLabel>Latitude</FormLabel>
                        <Input
                          value={form.latitude}
                          onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Longitude</FormLabel>
                        <Input
                          value={form.longitude}
                          onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                        />
                      </FormControl>
                    </SimpleGrid>
                    <FormControl>
                      <FormLabel>Plano</FormLabel>
                      <Select
                        value={form.plano_id}
                        onChange={(e) => setForm({ ...form, plano_id: e.target.value })}
                      >
                        <option value="">Sem plano</option>
                        {planos.map((plano) => (
                          <option key={plano.id} value={plano.id}>
                            {plano.nome}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Periodo</FormLabel>
                      <Select
                        value={form.periodo}
                        onChange={(e) => setForm({ ...form, periodo: e.target.value })}
                      >
                        <option value="mensal">Mensal</option>
                        <option value="trimestral">Trimestral</option>
                        <option value="anual">Anual</option>
                      </Select>
                    </FormControl>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                      <FormControl>
                        <FormLabel>Inicio</FormLabel>
                        <Input
                          type="date"
                          value={form.data_inicio}
                          onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Fim</FormLabel>
                        <Input
                          type="date"
                          value={form.data_fim}
                          onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
                        />
                      </FormControl>
                    </SimpleGrid>
                    <FormControl>
                      <FormLabel>Valor</FormLabel>
                      <Input value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} />
                    </FormControl>
                  </Stack>
                </TabPanel>
                <TabPanel px={0}>
                  {editing ? (
                    <Box className="empresa-cameras-panel" p={3}>
                      <Text className="empresa-camera-panel-title" mb={3}>
                        Câmeras do cliente
                      </Text>
                      {camerasLoading ? (
                        <Skeleton height="40px" />
                      ) : camerasCliente.length ? (
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
                          {camerasCliente.map((cam) => (
                            <Box key={cam.id} p={3} className="empresa-camera-editor-card">
                              <HStack justify="space-between" align="center" mb={2} flexWrap="wrap">
                                <Text className="empresa-camera-header-title">
                                  Câmera #{cam.canal || cam.id}
                                </Text>
                                <Text className="empresa-camera-rtmp">
                                  RTMP: {cam.rtmp_link || "-"}
                                </Text>
                              </HStack>
                              <Stack spacing={3}>
                                <FormControl>
                                  <FormLabel>Nome</FormLabel>
                                  <Input
                                    size="sm"
                                    value={cam.nome || ""}
                                    onChange={(e) => handleCameraChange(cam.id, "nome", e.target.value)}
                                  />
                                </FormControl>
                                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                  <FormLabel mb="0" className="empresa-camera-switch-label">Ativa</FormLabel>
                                  <Switch
                                    isChecked={!!cam.ativa}
                                    onChange={(e) =>
                                      handleCameraChange(cam.id, "ativa", e.target.checked ? 1 : 0)
                                    }
                                  />
                                </FormControl>
                                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                  <FormLabel mb="0" className="empresa-camera-switch-label">Publica</FormLabel>
                                  <Switch
                                    isChecked={Number(cam.flag_publico) === 1}
                                    onChange={(e) =>
                                      handleCameraChange(cam.id, "flag_publico", e.target.checked ? 1 : 0)
                                    }
                                  />
                                </FormControl>
                                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                  <FormLabel mb="0" className="empresa-camera-switch-label">Assistente IA</FormLabel>
                                  <Switch
                                    isChecked={!!cam.ia_ativo}
                                    onChange={(e) =>
                                      handleCameraChange(cam.id, "ia_ativo", e.target.checked ? 1 : 0)
                                    }
                                  />
                                </FormControl>
                                <SimpleGrid columns={2} spacing={2}>
                                  <FormControl>
                                    <FormLabel>Inicio IA</FormLabel>
                                    <Input
                                      type="time"
                                      size="sm"
                                      value={formatHora(cam.ia_hora_inicio)}
                                      onChange={(e) => handleCameraChange(cam.id, "ia_hora_inicio", e.target.value)}
                                      isDisabled={!cam.ia_ativo}
                                    />
                                  </FormControl>
                                  <FormControl>
                                    <FormLabel>Fim IA</FormLabel>
                                    <Input
                                      type="time"
                                      size="sm"
                                      value={formatHora(cam.ia_hora_fim)}
                                      onChange={(e) => handleCameraChange(cam.id, "ia_hora_fim", e.target.value)}
                                      isDisabled={!cam.ia_ativo}
                                    />
                                  </FormControl>
                                </SimpleGrid>
                                <Button size="sm" className="empresa-camera-save-btn" onClick={() => handleCameraSave(cam)}>
                                  Salvar camera
                                </Button>
                              </Stack>
                            </Box>
                          ))}
                        </SimpleGrid>
                      ) : (
                        <Text fontSize="sm" color="rgba(220, 247, 239, 0.84)">
                          Nenhuma camera encontrada para este cliente.
                        </Text>
                      )}
                    </Box>
                  ) : (
                    <Text fontSize="sm" color="rgba(220, 247, 239, 0.84)">
                      Salve o cliente primeiro para gerenciar câmeras.
                    </Text>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={modal.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="green" onClick={handleSave}>
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};
const EmpresaAfiliadas = ({ onEnterEmpresa }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0 });
  const [filters, setFilters] = useState({ q: "", status: "" });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    nome: "",
    slug: "",
    status: "ativo",
    pai_id: "",
    admin_nome: "",
    admin_email: "",
    admin_senha: ""
  });
  const modal = useDisclosure();
  const toast = useToast();
  const localUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isRoot = Number(localUser?.empresa_id) === 1;

  const load = async (page = pagination.page) => {
    setLoading(true);
    try {
      const [treeRes, listRes] = await Promise.all([
        api.get("/v2/empresas?tree=true"),
        api.get("/v2/empresas", {
          params: {
            page,
            page_size: pagination.page_size,
            q: filters.q || undefined,
            status: filters.status || undefined
          }
        })
      ]);
      setTree(treeRes.data.tree || null);
      setEmpresas(listRes.data.items || []);
      setPagination({
        page: listRes.data.page,
        page_size: listRes.data.page_size,
        total: listRes.data.total
      });
    } catch (err) {
      console.error("Erro ao carregar afiliadas:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      nome: "",
      slug: "",
      status: "ativo",
      pai_id: "",
      admin_nome: "",
      admin_email: "",
      admin_senha: ""
    });
    modal.onOpen();
  };

  const openEdit = (empresa) => {
    setEditing(empresa);
    setForm({
      nome: empresa.nome || "",
      slug: empresa.slug || "",
      status: empresa.status || "ativo",
      pai_id: "",
      admin_nome: "",
      admin_email: "",
      admin_senha: ""
    });
    modal.onOpen();
    api
      .get(`/v2/empresas/${empresa.id}`)
      .then((response) => {
        const admin = response.data?.admin_user || {};
        setForm((prev) => ({
          ...prev,
          admin_nome: admin.nome || prev.admin_nome,
          admin_email: admin.email || prev.admin_email
        }));
      })
      .catch(() => {});
  };

  const handleSave = async () => {
    try {
      if (editing) {
        const payload = {
          nome: form.nome,
          slug: form.slug,
          status: form.status
        };
        if (form.admin_nome) payload.admin_nome = form.admin_nome;
        if (form.admin_email) payload.admin_email = form.admin_email;
        if (form.admin_senha) payload.admin_senha = form.admin_senha;
        await api.put(`/v2/empresas/${editing.id}`, payload);
      } else {
        if (!form.admin_nome || !form.admin_email || !form.admin_senha) {
          toast({
            title: "Preencha os dados de acesso",
            status: "warning",
            duration: 3000,
            isClosable: true
          });
          return;
        }
        await api.post("/v2/empresas", {
          nome: form.nome,
          slug: form.slug,
          pai_id: isRoot ? form.pai_id || undefined : undefined,
          admin_nome: form.admin_nome,
          admin_email: form.admin_email,
          admin_senha: form.admin_senha
        });
      }
      toast({ title: "Empresa salva", status: "success", duration: 3000, isClosable: true });
      modal.onClose();
      load();
    } catch (err) {
      toast({ title: "Erro ao salvar", status: "error", duration: 3000, isClosable: true });
    }
  };

  const handleDelete = async (empresa) => {
    try {
      await api.delete(`/v2/empresas/${empresa.id}`);
      toast({ title: "Empresa desativada", status: "success", duration: 3000, isClosable: true });
      load();
    } catch (err) {
      toast({ title: "Erro ao desativar", status: "error", duration: 3000, isClosable: true });
    }
  };

  const handleEnterEmpresa = (empresa, targetTab = "resumo") => {
    if (!empresa?.id) return;
    localStorage.setItem("active_empresa_id", String(empresa.id));
    localStorage.setItem("empresa_scope", "self");
    localStorage.setItem("empresa_admin_tab", targetTab);
    if (typeof onEnterEmpresa === "function") {
      onEnterEmpresa(empresa, targetTab);
      return;
    }
    navigate("/sistema/empresa");
  };

  const renderNode = (node, level = 0) => {
    if (!node) return null;
    return (
      <Box
        key={node.id}
        pl={level * 4}
        py={2}
        borderLeft={level ? "1px solid" : "none"}
        borderColor="rgba(171, 236, 232, 0.22)"
      >
        <Text fontWeight="semibold" color="rgba(239, 255, 250, 0.94)">
          {node.nome}
        </Text>
        <Text fontSize="xs" color="rgba(207, 240, 233, 0.74)">
          ID {node.id}
        </Text>
        {(node.filhas || []).map((filha) => renderNode(filha, level + 1))}
      </Box>
    );
  };

  return (
    <Stack spacing={4}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Text fontWeight="bold" fontSize="lg">
          Afiliadas
        </Text>
        <HStack spacing={2} flexWrap="wrap">
          <Input
            placeholder="Buscar por nome/slug"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="empresa-input-control"
          />
          <Select
            placeholder="Status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            maxW="160px"
            className="empresa-input-control"
          >
            <option value="ativo">Ativo</option>
            <option value="suspenso">Suspenso</option>
          </Select>
          <Button className="empresa-action-btn" onClick={() => load(1)}>
            Filtrar
          </Button>
          <Button className="empresa-primary-btn" onClick={openNew}>
            Nova afiliada
          </Button>
        </HStack>
      </Flex>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Box className="empresa-panel-card" p={4}>
          {tree ? renderNode(tree) : <Skeleton height="24px" />}
        </Box>
        <Box className="empresa-panel-card" overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Nome</Th>
                <Th>Status</Th>
                <Th>Acoes</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={3}>
                    <Skeleton height="20px" />
                  </Td>
                </Tr>
              ) : (
                empresas.map((empresa) => (
                  <Tr key={empresa.id}>
                    <Td>{empresa.nome}</Td>
                    <Td>{empresa.status}</Td>
                    <Td>
                      <HStack spacing={1.5} flexWrap="wrap">
                        <Button
                          size="xs"
                          className="empresa-primary-btn"
                          onClick={() => handleEnterEmpresa(empresa, "clientes")}
                        >
                          Entrar
                        </Button>
                        <Button size="xs" className="empresa-action-btn" onClick={() => openEdit(empresa)}>
                          Editar
                        </Button>
                        {empresa.id !== 1 && (
                          <Button size="xs" colorScheme="red" onClick={() => handleDelete(empresa)}>
                            Excluir
                          </Button>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
          <Pagination
            page={pagination.page}
            pageSize={pagination.page_size}
            total={pagination.total}
            onPageChange={(next) => load(next)}
          />
        </Box>
      </SimpleGrid>

      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent className="empresa-modal-content">
          <ModalHeader>{editing ? "Editar empresa" : "Nova afiliada"}</ModalHeader>
          <ModalBody>
            <Stack spacing={3}>
              <FormControl isRequired>
                <FormLabel>Nome</FormLabel>
                <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Slug</FormLabel>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              </FormControl>
              {editing ? (
                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    <option value="ativo">Ativo</option>
                    <option value="suspenso">Suspenso</option>
                  </Select>
                </FormControl>
              ) : (
                <>
                  {isRoot ? (
                    <FormControl>
                      <FormLabel>Empresa pai</FormLabel>
                      <Select value={form.pai_id} onChange={(e) => setForm({ ...form, pai_id: e.target.value })}>
                        <option value="">Usar empresa atual</option>
                        {empresas.map((empresa) => (
                          <option key={empresa.id} value={empresa.id}>
                            {empresa.nome}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <FormControl>
                      <FormLabel>Empresa pai</FormLabel>
                      <Input value={localUser?.empresa_nome || localUser?.empresa_id || ""} isReadOnly />
                    </FormControl>
                  )}
                </>
              )}

              <Divider />
              <Text fontWeight="semibold">
                {editing ? "Acesso do admin" : "Acesso da nova empresa"}
              </Text>
              <FormControl isRequired={!editing}>
                <FormLabel>Nome do admin</FormLabel>
                <Input
                  value={form.admin_nome}
                  onChange={(e) => setForm({ ...form, admin_nome: e.target.value })}
                />
              </FormControl>
              <FormControl isRequired={!editing}>
                <FormLabel>Email do admin</FormLabel>
                <Input
                  value={form.admin_email}
                  onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                />
              </FormControl>
              <FormControl isRequired={!editing}>
                <FormLabel>Senha do admin</FormLabel>
                <Input
                  type="password"
                  placeholder={editing ? "Deixe em branco para manter" : undefined}
                  value={form.admin_senha}
                  onChange={(e) => setForm({ ...form, admin_senha: e.target.value })}
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={modal.onClose}>
              Cancelar
            </Button>
            <Button className="empresa-primary-btn" onClick={handleSave}>
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};
const EmpresaPlanos = () => {
  const [loading, setLoading] = useState(true);
  const [planos, setPlanos] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0 });
  const [filters, setFilters] = useState({ q: "" });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    nome: "",
    tier: "",
    preco_mensal: "",
    preco_trimestral: "",
    preco_anual: "",
    raio_km: "",
    limite_usuarios: "",
    visibilidade_total: false
  });
  const modal = useDisclosure();
  const toast = useToast();

  const load = async (page = pagination.page) => {
    setLoading(true);
    try {
      const response = await api.get("/v2/planos", {
        params: { page, page_size: pagination.page_size, q: filters.q || undefined }
      });
      setPlanos(response.data.items || []);
      setPagination({
        page: response.data.page,
        page_size: response.data.page_size,
        total: response.data.total
      });
    } catch (err) {
      console.error("Erro ao carregar planos:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      nome: "",
      tier: "",
      preco_mensal: "",
      preco_trimestral: "",
      preco_anual: "",
      raio_km: "",
      limite_usuarios: "",
      visibilidade_total: false
    });
    modal.onOpen();
  };

  const openEdit = (plano) => {
    setEditing(plano);
    setForm({
      nome: plano.nome || "",
      tier: plano.tier || "",
      preco_mensal: plano.preco_mensal ?? plano.preco ?? "",
      preco_trimestral: plano.preco_trimestral ?? "",
      preco_anual: plano.preco_anual ?? "",
      raio_km: plano.raio_km || "",
      limite_usuarios: plano.limite_usuarios || "",
      visibilidade_total: Boolean(plano.visibilidade_total)
    });
    modal.onOpen();
  };

  const handleSave = async () => {
    try {
      const payload = {
        nome: form.nome,
        tier: form.tier || null,
        preco: form.preco_mensal || null,
        preco_mensal: form.preco_mensal || null,
        preco_trimestral: form.preco_trimestral || null,
        preco_anual: form.preco_anual || null,
        raio_km: form.raio_km || null,
        limite_usuarios: form.limite_usuarios || null,
        visibilidade_total: form.visibilidade_total
      };

      if (editing) {
        await api.put(`/v2/planos/${editing.id}`, payload);
      } else {
        await api.post("/v2/planos", payload);
      }

      toast({ title: "Plano salvo", status: "success", duration: 3000, isClosable: true });
      modal.onClose();
      load();
    } catch (err) {
      toast({ title: "Erro ao salvar", status: "error", duration: 3000, isClosable: true });
    }
  };

  const handleDelete = async (plano) => {
    try {
      await api.delete(`/v2/planos/${plano.id}`);
      toast({ title: "Plano removido", status: "success", duration: 3000, isClosable: true });
      load();
    } catch (err) {
      toast({ title: "Erro ao remover", status: "error", duration: 3000, isClosable: true });
    }
  };

  return (
    <Stack spacing={4}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Text fontWeight="bold" fontSize="lg">
          Planos
        </Text>
        <HStack spacing={2} flexWrap="wrap">
          <Input
            placeholder="Buscar por nome/nivel"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="empresa-input-control"
          />
          <Button className="empresa-action-btn" onClick={() => load(1)}>
            Buscar
          </Button>
          <Button className="empresa-primary-btn" onClick={openNew}>
            Novo plano
          </Button>
        </HStack>
      </Flex>
      <Box className="empresa-panel-card" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Nome</Th>
              <Th>Nivel</Th>
              <Th>Mensal</Th>
              <Th>Trimestral</Th>
              <Th>Anual</Th>
              <Th>Raio km</Th>
              <Th>Limite de usuários</Th>
              <Th>Visibilidade total</Th>
              <Th>Acoes</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={9}>
                  <Skeleton height="20px" />
                </Td>
              </Tr>
            ) : (
              planos.map((plano) => (
                <Tr key={plano.id}>
                  <Td>{plano.nome}</Td>
                  <Td>{plano.tier || "-"}</Td>
                  <Td>{plano.preco_mensal != null ? `R$ ${Number(plano.preco_mensal).toFixed(2)}` : "-"}</Td>
                  <Td>
                    {plano.preco_trimestral != null ? `R$ ${Number(plano.preco_trimestral).toFixed(2)}` : "-"}
                  </Td>
                  <Td>{plano.preco_anual != null ? `R$ ${Number(plano.preco_anual).toFixed(2)}` : "-"}</Td>
                  <Td>{plano.raio_km || "-"}</Td>
                  <Td>{plano.limite_usuarios || "-"}</Td>
                  <Td>{plano.visibilidade_total ? "Sim" : "Não"}</Td>
                  <Td>
                    <HStack spacing={1.5} flexWrap="wrap">
                      <Button size="xs" className="empresa-action-btn" onClick={() => openEdit(plano)}>
                        Editar
                      </Button>
                      <Button size="xs" colorScheme="red" onClick={() => handleDelete(plano)}>
                        Excluir
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
        <Pagination
          page={pagination.page}
          pageSize={pagination.page_size}
          total={pagination.total}
          onPageChange={(next) => load(next)}
        />
      </Box>

      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent className="empresa-modal-content">
          <ModalHeader>{editing ? "Editar plano" : "Novo plano"}</ModalHeader>
          <ModalBody>
            <Stack spacing={3}>
              <FormControl isRequired>
                <FormLabel>Nome</FormLabel>
                <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Nivel</FormLabel>
                <Input value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} />
              </FormControl>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                <FormControl>
                  <FormLabel>Preco mensal</FormLabel>
                  <Input
                    value={form.preco_mensal}
                    onChange={(e) => setForm({ ...form, preco_mensal: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Preco trimestral</FormLabel>
                  <Input
                    value={form.preco_trimestral}
                    onChange={(e) => setForm({ ...form, preco_trimestral: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Preco anual</FormLabel>
                  <Input
                    value={form.preco_anual}
                    onChange={(e) => setForm({ ...form, preco_anual: e.target.value })}
                  />
                </FormControl>
              </SimpleGrid>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl>
                  <FormLabel>Raio km</FormLabel>
                  <Input value={form.raio_km} onChange={(e) => setForm({ ...form, raio_km: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel>Limite de usuários</FormLabel>
                  <Input
                    value={form.limite_usuarios}
                    onChange={(e) => setForm({ ...form, limite_usuarios: e.target.value })}
                  />
                </FormControl>
              </SimpleGrid>
              <FormControl display="flex" alignItems="center">
                <Switch
                  isChecked={form.visibilidade_total}
                  onChange={(e) => setForm({ ...form, visibilidade_total: e.target.checked })}
                  mr={2}
                />
                <FormLabel mb={0}>Visibilidade total</FormLabel>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={modal.onClose}>
              Cancelar
            </Button>
            <Button className="empresa-primary-btn" onClick={handleSave}>
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};
const EmpresaCompartilhamentos = () => {
  const [loading, setLoading] = useState(true);
  const [shares, setShares] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0 });
  const [filters, setFilters] = useState({ status: "", escopo: "" });
  const [form, setForm] = useState({
    empresa_destino_id: "",
    escopo: "camera",
    resource_id: "",
    data_inicio: "",
    data_fim: ""
  });
  const modal = useDisclosure();
  const toast = useToast();

  const load = async (page = pagination.page) => {
    setLoading(true);
    try {
      const response = await api.get("/v2/share-rules", {
        params: {
          page,
          page_size: pagination.page_size,
          status: filters.status || undefined,
          escopo: filters.escopo || undefined
        }
      });
      setShares(response.data.items || []);
      setPagination({
        page: response.data.page,
        page_size: response.data.page_size,
        total: response.data.total
      });
    } catch (err) {
      console.error("Erro ao carregar compartilhamentos:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, []);

  const handleSave = async () => {
    try {
      await api.post("/v2/share-rules", {
        empresa_destino_id: form.empresa_destino_id,
        escopo: form.escopo,
        resource_id: form.resource_id || null,
        data_inicio: form.data_inicio || null,
        data_fim: form.data_fim || null
      });
      toast({ title: "Compartilhamento criado", status: "success", duration: 3000, isClosable: true });
      modal.onClose();
      load();
    } catch (err) {
      toast({ title: "Erro ao criar", status: "error", duration: 3000, isClosable: true });
    }
  };

  const handleDelete = async (rule) => {
    try {
      await api.delete(`/v2/share-rules/${rule.id}`);
      toast({ title: "Compartilhamento removido", status: "success", duration: 3000, isClosable: true });
      load();
    } catch (err) {
      toast({ title: "Erro ao remover", status: "error", duration: 3000, isClosable: true });
    }
  };

  return (
    <Stack spacing={4}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Text fontWeight="bold" fontSize="lg">
          Compartilhamentos
        </Text>
        <HStack spacing={2} flexWrap="wrap">
          <Select
            placeholder="Status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            maxW="160px"
            className="empresa-input-control"
          >
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
          </Select>
          <Select
            placeholder="Escopo"
            value={filters.escopo}
            onChange={(e) => setFilters({ ...filters, escopo: e.target.value })}
            maxW="160px"
            className="empresa-input-control"
          >
            <option value="camera">Câmera</option>
            <option value="usuario">Usuário</option>
            <option value="area">Area</option>
          </Select>
          <Button className="empresa-action-btn" onClick={() => load(1)}>
            Filtrar
          </Button>
          <Button className="empresa-primary-btn" onClick={modal.onOpen}>
            Novo compartilhamento
          </Button>
        </HStack>
      </Flex>
      <Box className="empresa-panel-card" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Origem</Th>
              <Th>Destino</Th>
              <Th>Escopo</Th>
              <Th>Status</Th>
              <Th>Acoes</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={5}>
                  <Skeleton height="20px" />
                </Td>
              </Tr>
            ) : (
              shares.map((rule) => (
                <Tr key={rule.id}>
                  <Td>{rule.empresa_origem_id}</Td>
                  <Td>{rule.empresa_destino_id}</Td>
                  <Td>{rule.escopo}</Td>
                  <Td>{rule.status}</Td>
                  <Td>
                    <Button size="xs" colorScheme="red" onClick={() => handleDelete(rule)}>
                      Excluir
                    </Button>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
        <Pagination
          page={pagination.page}
          pageSize={pagination.page_size}
          total={pagination.total}
          onPageChange={(next) => load(next)}
        />
      </Box>

      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent className="empresa-modal-content">
          <ModalHeader>Novo compartilhamento</ModalHeader>
          <ModalBody>
            <Stack spacing={3}>
              <FormControl isRequired>
                <FormLabel>Empresa destino</FormLabel>
                <Input
                  value={form.empresa_destino_id}
                  onChange={(e) => setForm({ ...form, empresa_destino_id: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Escopo</FormLabel>
                <Select value={form.escopo} onChange={(e) => setForm({ ...form, escopo: e.target.value })}>
                  <option value="camera">Câmera</option>
                  <option value="usuario">Usuário</option>
                  <option value="area">Area</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Resource ID</FormLabel>
                <Input value={form.resource_id} onChange={(e) => setForm({ ...form, resource_id: e.target.value })} />
              </FormControl>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl>
                  <FormLabel>Inicio</FormLabel>
                  <Input
                    type="datetime-local"
                    value={form.data_inicio}
                    onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Fim</FormLabel>
                  <Input
                    type="datetime-local"
                    value={form.data_fim}
                    onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
                  />
                </FormControl>
              </SimpleGrid>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={modal.onClose}>
              Cancelar
            </Button>
            <Button className="empresa-primary-btn" onClick={handleSave}>
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

const EmpresaFinanceiro = () => {
  const [assinaturas, setAssinaturas] = useState([]);
  const [pagamentos, setPagamentos] = useState([]);
  const [paginationAss, setPaginationAss] = useState({ page: 1, page_size: 10, total: 0 });
  const [paginationPag, setPaginationPag] = useState({ page: 1, page_size: 10, total: 0 });
  const [filters, setFilters] = useState({ q: "", status: "" });
  const [loading, setLoading] = useState(true);

  const loadAssinaturas = async (page = paginationAss.page) => {
    const response = await api.get("/v2/assinaturas", {
      params: {
        page,
        page_size: paginationAss.page_size,
        q: filters.q || undefined,
        status: filters.status || undefined
      }
    });
    setAssinaturas(response.data.items || []);
    setPaginationAss({
      page: response.data.page,
      page_size: response.data.page_size,
      total: response.data.total
    });
  };

  const loadPagamentos = async (page = paginationPag.page) => {
    const response = await api.get("/v2/pagamentos", {
      params: { page, page_size: paginationPag.page_size, status: filters.status || undefined }
    });
    setPagamentos(response.data.items || []);
    setPaginationPag({
      page: response.data.page,
      page_size: response.data.page_size,
      total: response.data.total
    });
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await Promise.all([loadAssinaturas(1), loadPagamentos(1)]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const receitaEstimada = useMemo(() => {
    return assinaturas.reduce((acc, item) => acc + Number(item.valor || 0), 0);
  }, [assinaturas]);

  return (
    <Stack spacing={4}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Text fontWeight="bold" fontSize="lg">
          Financeiro
        </Text>
        <HStack spacing={2} flexWrap="wrap">
          <Input
            placeholder="Buscar cliente"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="empresa-input-control"
          />
          <Select
            placeholder="Status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            maxW="160px"
            className="empresa-input-control"
          >
            <option value="ativa">Ativa</option>
            <option value="cancelada">Cancelada</option>
            <option value="suspensa">Suspensa</option>
          </Select>
          <Button className="empresa-action-btn" onClick={() => Promise.all([loadAssinaturas(1), loadPagamentos(1)])}>
            Filtrar
          </Button>
        </HStack>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <Card title="Assinaturas ativas" value={assinaturas.length} help="Base atual" />
        <Card title="Receita estimada" value={`R$ ${receitaEstimada.toFixed(2)}`} help="Mensal" />
        <Card title="Pagamentos" value={paginationPag.total} help="Registrados" colorScheme="blue" />
      </SimpleGrid>

      <Box className="empresa-panel-card" overflowX="auto">
        <Text fontWeight="semibold" px={4} pt={4}>
          Assinaturas
        </Text>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Cliente</Th>
              <Th>Plano</Th>
              <Th>Periodo</Th>
              <Th>Inicio</Th>
              <Th>Fim</Th>
              <Th>Status</Th>
              <Th>Valor</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={7}>
                  <Skeleton height="20px" />
                </Td>
              </Tr>
            ) : (
              assinaturas.map((item) => (
                <Tr key={item.id}>
                  <Td>{item.usuario_nome || "-"}</Td>
                  <Td>{item.plano_nome || item.plano_id}</Td>
                  <Td>{item.periodo || "-"}</Td>
                  <Td>{item.data_inicio ? item.data_inicio.slice(0, 10) : "-"}</Td>
                  <Td>{item.data_fim ? item.data_fim.slice(0, 10) : "-"}</Td>
                  <Td>{item.status}</Td>
                  <Td>R$ {Number(item.valor || 0).toFixed(2)}</Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
        <Pagination
          page={paginationAss.page}
          pageSize={paginationAss.page_size}
          total={paginationAss.total}
          onPageChange={(next) => loadAssinaturas(next)}
        />
      </Box>

      <Box className="empresa-panel-card" overflowX="auto">
        <Text fontWeight="semibold" px={4} pt={4}>
          Pagamentos
        </Text>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Valor</Th>
              <Th>Status</Th>
              <Th>Gateway</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={4}>
                  <Skeleton height="20px" />
                </Td>
              </Tr>
            ) : (
              pagamentos.map((item) => (
                <Tr key={item.id}>
                  <Td>{item.id}</Td>
                  <Td>R$ {Number(item.valor || 0).toFixed(2)}</Td>
                  <Td>{item.status}</Td>
                  <Td>{item.gateway_ref || "-"}</Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
        <Pagination
          page={paginationPag.page}
          pageSize={paginationPag.page_size}
          total={paginationPag.total}
          onPageChange={(next) => loadPagamentos(next)}
        />
      </Box>
    </Stack>
  );
};
const EmpresaComissões = () => {
  const [planos, setPlanos] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0 });

  useEffect(() => {
    const load = async () => {
      try {
        const planosRes = await api.get("/v2/comissoes/planos");
        setPlanos(planosRes.data || []);
        await loadLedger(1);
      } catch (err) {
        console.error("Erro ao carregar comissoes:", err);
      }
    };

    load();
  }, []);

  const loadLedger = async (page = pagination.page) => {
    const ledgerRes = await api.get("/v2/comissoes/ledger", {
      params: { page, page_size: pagination.page_size }
    });
    setLedger(ledgerRes.data.items || []);
    setPagination({
      page: ledgerRes.data.page,
      page_size: ledgerRes.data.page_size,
      total: ledgerRes.data.total
    });
  };

  return (
    <Stack spacing={4}>
      <Text fontWeight="bold" fontSize="lg">
        Comissionamento
      </Text>
      <Box className="empresa-panel-card" p={4}>
        <Text fontWeight="semibold" mb={2}>
          Planos de comissao
        </Text>
        {planos.length === 0 ? (
          <Text fontSize="sm" color="rgba(207, 240, 233, 0.74)">
            Nenhum plano cadastrado.
          </Text>
        ) : (
          planos.map((plano) => (
            <Box key={plano.id} py={2} borderBottom="1px solid" borderColor="rgba(171, 236, 232, 0.2)">
              <Text>{plano.nome}</Text>
            </Box>
          ))
        )}
      </Box>
      <Box className="empresa-panel-card" overflowX="auto">
        <Text fontWeight="semibold" px={4} pt={4}>
          Ledger
        </Text>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Pagamento</Th>
              <Th>Nivel</Th>
              <Th>Percentual</Th>
              <Th>Valor</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {ledger.map((item) => (
              <Tr key={item.id}>
                <Td>{item.pagamento_id}</Td>
                <Td>{item.nivel}</Td>
                <Td>{item.percentual}%</Td>
                <Td>R$ {Number(item.valor || 0).toFixed(2)}</Td>
                <Td>{item.status}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        <Pagination
          page={pagination.page}
          pageSize={pagination.page_size}
          total={pagination.total}
          onPageChange={(next) => loadLedger(next)}
        />
      </Box>
    </Stack>
  );
};

const EmpresaMapa = () => (
  <Box className="empresa-panel-card" overflow="hidden">
    <MapaCameras height="70vh" />
  </Box>
);

const EmpresaDashboard = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selected, setSelected] = useState(
    () => localStorage.getItem("empresa_admin_tab") || "resumo"
  );
  const [scopeMode, setScopeMode] = useState(localStorage.getItem("empresa_scope") || "all");
  const [activeEmpresaId, setActiveEmpresaId] = useState(() => {
    const parsed = Number(localStorage.getItem("active_empresa_id"));
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  });
  const [activeEmpresaNome, setActiveEmpresaNome] = useState("");
  const [contextLoading, setContextLoading] = useState(false);
  const navigate = useNavigate();

  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "{}"), []);
  const userEmpresaId = Number(user?.empresa_id) || 1;
  const role = String(user?.role || "").toLowerCase();
  const canManageAffiliates = role === "superadmin" || role === "owner";
  const effectiveEmpresaId = activeEmpresaId || userEmpresaId;
  const usingDelegatedContext =
    Number.isInteger(activeEmpresaId) && activeEmpresaId > 0 && activeEmpresaId !== userEmpresaId;

  const menuItemsVisible = useMemo(() => {
    if (canManageAffiliates) return menuItems;
    return menuItems.filter((item) =>
      ["resumo", "clientes", "mapa", "financeiro", "config"].includes(item.id)
    );
  }, [canManageAffiliates]);

  const handleMenuChange = (next) => {
    setSelected(next);
    localStorage.setItem("empresa_admin_tab", next);
  };

  const handleEnterEmpresaContext = (empresa, targetTab = "resumo") => {
    const nextId = Number(empresa?.id || empresa);
    if (!Number.isInteger(nextId) || nextId <= 0) return;
    localStorage.setItem("active_empresa_id", String(nextId));
    localStorage.setItem("empresa_scope", "self");
    setScopeMode("self");
    setActiveEmpresaId(nextId);
    setActiveEmpresaNome(empresa?.nome || "");
    if (targetTab) {
      handleMenuChange(targetTab);
    }
  };

  const handleResetEmpresaContext = () => {
    localStorage.removeItem("active_empresa_id");
    setActiveEmpresaId(null);
    setActiveEmpresaNome("");
    if (scopeMode !== "all") {
      localStorage.setItem("empresa_scope", "all");
      setScopeMode("all");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("active_empresa_id");
    localStorage.removeItem("empresa_scope");
    localStorage.removeItem("empresa_admin_tab");
    navigate("/sistema");
  };

  const handleScopeToggle = (checked) => {
    const next = checked ? "self" : "all";
    setScopeMode(next);
    localStorage.setItem("empresa_scope", next);
  };

  const openClientView = () => {
    localStorage.setItem("dashboard_target_tab", "home");
    navigate("/sistema/dashboard");
  };

  useEffect(() => {
    const targetTab = localStorage.getItem("empresa_admin_tab");
    if (targetTab) {
      setSelected(targetTab);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadEmpresaContext = async () => {
      setContextLoading(true);
      try {
        const response = await api.get(`/v2/empresas/${effectiveEmpresaId}`);
        if (mounted) {
          setActiveEmpresaNome(response.data?.nome || `Empresa #${effectiveEmpresaId}`);
        }
      } catch (err) {
        if (mounted) {
          setActiveEmpresaNome(
            user?.empresa_nome || user?.empresa || `Empresa #${effectiveEmpresaId}`
          );
        }
      } finally {
        if (mounted) {
          setContextLoading(false);
        }
      }
    };
    loadEmpresaContext();
    return () => {
      mounted = false;
    };
  }, [effectiveEmpresaId, user?.empresa, user?.empresa_nome]);

  const renderContent = () => {
    switch (selected) {
      case "clientes":
        return <EmpresaClientes />;
      case "afiliadas":
        return <EmpresaAfiliadas onEnterEmpresa={handleEnterEmpresaContext} />;
      case "planos":
        return <EmpresaPlanos />;
      case "compartilhamentos":
        return <EmpresaCompartilhamentos />;
      case "mapa":
        return <EmpresaMapa />;
      case "financeiro":
        return <EmpresaFinanceiro />;
      case "comissoes":
        return <EmpresaComissões />;
      case "landing":
        return <LandingAdminPage empresaId={effectiveEmpresaId} />;
      case "config":
        return <EmpresaConfig empresaId={effectiveEmpresaId} />;
      case "resumo":
      default:
        return <EmpresaResumo />;
    }
  };

  return (
    <Box className="empresa-admin-root">
      <Box className="empresa-admin-bg" />

      <Flex className="empresa-admin-header" align="center" justify="space-between" px={4} py={3} wrap="wrap">
        <HStack spacing={3}>
          <IconButton
            aria-label="Menu"
            icon={<FaBars />}
            onClick={onOpen}
            variant="ghost"
            className="empresa-menu-toggle"
          />
          <VStack align="flex-start" spacing={0}>
            <Text className="empresa-admin-title">Painel administrativo</Text>
            <Text className="empresa-admin-subtitle">Gestão de operação, clientes e configurações</Text>
          </VStack>
        </HStack>
        <HStack spacing={3} mt={{ base: 3, md: 0 }} flexWrap="wrap" justify={{ base: "flex-start", md: "flex-end" }}>
          <Badge className="empresa-role-badge">{role || "admin"}</Badge>
          <HStack spacing={2}>
            <Switch
              isChecked={scopeMode === "self"}
              onChange={(e) => handleScopeToggle(e.target.checked)}
              isDisabled={!canManageAffiliates}
            />
            <Text fontSize="sm" color="rgba(215, 247, 239, 0.92)">
              Escopo proprio
            </Text>
          </HStack>
          <Button className="empresa-action-btn" onClick={openClientView}>
            Ver como cliente
          </Button>
          <Button leftIcon={<FaSignOutAlt />} variant="ghost" colorScheme="red" onClick={handleLogout}>
            Sair
          </Button>
        </HStack>
      </Flex>

      <Flex className="empresa-admin-main">
        <Box
          display={{ base: "none", md: "block" }}
          w="264px"
          className="empresa-sidebar"
          minH="calc(100vh - 80px)"
          p={4}
        >
          <VStack align="stretch" spacing={2}>
            {menuItemsVisible.map((item) => {
              const Icon = item.icon;
              const active = selected === item.id;
              return (
                <Button
                  key={item.id}
                  leftIcon={<Icon />}
                  className={`empresa-menu-btn ${active ? "is-active" : ""}`}
                  justifyContent="flex-start"
                  onClick={() => handleMenuChange(item.id)}
                >
                  {item.label}
                </Button>
              );
            })}
          </VStack>
        </Box>

        <Box flex="1" p={{ base: 4, md: 6 }} className="empresa-content-wrap">
          <Box className="empresa-context-strip">
            <HStack spacing={3} flexWrap="wrap">
              <Text className="empresa-context-title">
                Empresa ativa: {contextLoading ? "carregando..." : activeEmpresaNome || `#${effectiveEmpresaId}`}
              </Text>
              {usingDelegatedContext && (
                <Badge className="empresa-context-badge">contexto delegado</Badge>
              )}
            </HStack>
            <HStack spacing={2} flexWrap="wrap" mt={{ base: 2, md: 0 }}>
              {usingDelegatedContext && (
                <Button size="sm" className="empresa-action-btn" onClick={handleResetEmpresaContext}>
                  Voltar para minha empresa
                </Button>
              )}
              {canManageAffiliates && (
                <Button size="sm" className="empresa-primary-btn" onClick={() => handleMenuChange("afiliadas")}>
                  Entrar em empresa
                </Button>
              )}
            </HStack>
          </Box>

          <MotionBox
            key={`${selected}-${effectiveEmpresaId}-${scopeMode}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderContent()}
          </MotionBox>
        </Box>
      </Flex>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent className="empresa-drawer-shell">
          <DrawerHeader>Menu administrativo</DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={2}>
              {menuItemsVisible.map((item) => {
                const Icon = item.icon;
                const active = selected === item.id;
                return (
                  <Button
                    key={item.id}
                    leftIcon={<Icon />}
                    className={`empresa-menu-btn ${active ? "is-active" : ""}`}
                    justifyContent="flex-start"
                    onClick={() => {
                      handleMenuChange(item.id);
                      onClose();
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
              <Divider />
              <Button className="empresa-action-btn" onClick={openClientView}>
                Ver como cliente
              </Button>
              <Button leftIcon={<FaSignOutAlt />} variant="ghost" colorScheme="red" onClick={handleLogout}>
                Sair
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default EmpresaDashboard;


